import {
  BOARD_LINK,
  UI,
  apiErrorLabel,
  type CallView,
  type DeliveryState,
  type Lang,
  type MessageOverrides,
  type MessagesPatch,
  type NumberMode,
  type OpeningHours,
  type OverrideKind,
  type PosterSize,
  type PublicBoard,
  type StaffDeviceStatus,
} from '@ringring/core';

export const API_BASE: string = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8787';
export const WS_BASE = API_BASE.replace(/^http/, 'ws');

/** The screen language every call carries; `useLang` (lang.tsx) sets it before the first screen fetches. */
let requestLang: Lang = 'fr';

export function setRequestLang(lang: Lang): void {
  requestLang = lang;
}

/** The code of an ApiError thrown when the API could not be reached at all (offline, DNS, CORS): status 0. */
export const NETWORK_ERROR = 'network';

/**
 * What a screen shows for a refused or failed call, in the screen language: the connection line when the API was not
 * reached, else the copy of the API error code (the generic line for an unknown one). Screens keep the code and call
 * this at render time, so switching language relabels an error already on screen. Never the raw code or status.
 */
export function errorText(lang: Lang, code: string | null | undefined): string {
  return code === NETWORK_ERROR ? UI[lang].offline : apiErrorLabel(lang, code);
}

/** Error body fields the API adds next to the code (attempts_left and retry_after on PIN sign-in). */
export interface ApiErrorDetails {
  code?: string;
  label?: string;
  message?: string;
  attempts_left?: number;
  retry_after?: number;
  /** A refused message text (issue #20): which language, which message and the variable at fault. */
  message_lang?: Lang | null;
  message_kind?: OverrideKind | null;
  placeholder?: string | null;
}

export interface ApiError extends Error {
  status: number;
  code?: string;
  details: ApiErrorDetails;
}

function apiFailure(status: number, details: ApiErrorDetails): ApiError {
  return Object.assign(new Error(errorText(requestLang, details.code)), { status, code: details.code, details });
}

/**
 * One call to the API. The screen language goes out as `Accept-Language`, so the API labels a refusal in it too. The
 * error thrown carries the code and its label in the screen language as `message` (errorText), never a raw status
 * or a browser's own English network message.
 */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { credentials: 'include', ...init, headers: { 'Content-Type': 'application/json', 'Accept-Language': requestLang, ...(init.headers ?? {}) } });
  } catch {
    throw apiFailure(0, { code: NETWORK_ERROR });
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: ApiErrorDetails | string };
    const details = typeof body.error === 'object' && body.error !== null ? body.error : {};
    throw apiFailure(res.status, details);
  }
  return res.json() as Promise<T>;
}

/**
 * Download the A4 or A5 poster of a pickup point (issue #13): the PDF is fetched with the session cookie, like every
 * dashboard call, then saved under `fileName` through a temporary object URL. Throws the ApiError of a refusal.
 */
export async function downloadPoster(venueId: string, pickupPointId: string, size: PosterSize, fileName: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/account/venues/${encodeURIComponent(venueId)}/pickup-points/${encodeURIComponent(pickupPointId)}/poster?size=${size}`, { credentials: 'include', headers: { 'Accept-Language': requestLang } });
  } catch {
    throw apiFailure(0, { code: NETWORK_ERROR });
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: ApiErrorDetails | string };
    throw apiFailure(res.status, typeof body.error === 'object' && body.error !== null ? body.error : {});
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** One venue in the switcher: the role there and the pickup points still taking orders. */
export interface VenueAccess {
  id: string;
  name: string;
  city: string | null;
  number_mode: NumberMode;
  org_id: string;
  role: 'owner' | 'manager';
  /** The board's language and first voice; the dashboard names it next to the second-language setting (issue #19). */
  default_lang: Lang;
  pickup_points: { id: string; name: string; voice_lang_2: Lang | null }[];
}

/** GET /auth/me: the signed-in owner or manager and the venues they can open. */
export interface Me {
  user: { id: string; phone: string; phone_display: string; name: string | null; email: string | null; lang: string };
  session: { id: string; created_at: number; expires_at: number };
  owner_of_orgs: string[];
  venues: VenueAccess[];
}

/** A pickup point as the dashboard shows it: the customer page behind its QR code, the staff screen and the TV board. */
export interface PickupPoint {
  id: string;
  name: string;
  archived_at: number | null;
  /** The TV board's optional second voice language; null is the venue default only (issue #19). */
  voice_lang_2: Lang | null;
  created_at: number;
  customer_url: string;
  staff_url: string;
  /** The TV board's signed link (`#/board/<id>?key=<key>`, issue #16); revoking it answers a new one. */
  board_url: string;
}

/** GET /account/venues/:venue */
export interface Venue {
  id: string;
  org_id: string;
  name: string;
  city: string | null;
  number_mode: NumberMode;
  default_lang: Lang;
  languages: Lang[];
  opening_hours: OpeningHours | null;
  sms_quota: number;
  sms_used: number;
  google_place_id: string | null;
  feedback_enabled: boolean;
  google_link_enabled: boolean;
  registrations_paused: boolean;
  /** The poster's secondary language as chosen by the owner (issue #13); null for automatic. */
  poster_lang_2: Lang | null;
  /** The poster's languages as printed: the default first, then the secondary one when the venue has another language. */
  poster_langs: Lang[];
  has_staff_pin: boolean;
  /** When the current PIN was generated from the dashboard; null for no PIN or a PIN that predates it (the seed). */
  staff_pin_set_at: number | null;
  /** Absolute URL of the venue logo (issue #20), null for none. */
  logo_url: string | null;
  /** The venue's own registration and ready texts per language (issue #20); a missing entry is the default. */
  message_overrides: MessageOverrides;
  role: 'owner' | 'manager';
  pickup_points: PickupPoint[];
}

/** GET /account/venues/:venue/staff-devices: one browser that entered the PIN (issue #12). Never the user agent. */
export interface StaffDevice {
  id: string;
  /** "iPhone · Safari"; null when the user agent said nothing. */
  label: string | null;
  pickup_point: { id: string; name: string | null } | null;
  first_seen_at: number;
  last_seen_at: number;
  expires_at: number;
  status: StaffDeviceStatus;
  revoked_at: number | null;
  revoked_reason: string | null;
}

/** POST /account/venues/:venue/staff-pin: the PIN is in this response and nowhere else. */
export interface NewStaffPin {
  pin: string;
  set_at: number;
  devices_signed_out: number;
}

/** PATCH /account/venues/:venue (issue #11): every field is optional; the settings form sends them all. */
export interface VenueSettingsBody {
  name?: string;
  city?: string;
  number_mode?: NumberMode;
  languages?: Lang[];
  default_lang?: Lang;
  opening_hours?: Record<string, { open: string; close: string }[]>;
  registrations_paused?: boolean;
  google_place_id?: string | null;
  feedback_enabled?: boolean;
  google_link_enabled?: boolean;
  /** Only the texts that changed (issue #20): a text, or null for the default. */
  messages?: MessagesPatch;
  poster_lang_2?: Lang | null;
}

/** The venue part of POST /account/organizations and the whole body of POST /account/organizations/:org/venues. */
export interface VenueSetupBody {
  name: string;
  city: string;
  opening_hours: Record<string, { open: string; close: string }[]>;
  number_mode: NumberMode;
  languages: Lang[];
  default_lang: Lang;
  pickup_point?: string;
}

/**
 * GET /board/:pp?key=<board key>: what the TV board needs besides the socket. The board reads and speaks in the venue's
 * default language; `voice_langs` is that language followed by the pickup point's optional second language. Without
 * the board link's key the API answers 401 `board_link_required`, with a key that no longer matches 401
 * `board_link_invalid` (issue #16).
 */
export interface BoardSettings {
  pickup_point: { id: string; name: string; voice_lang_2: Lang | null };
  /** `logo_url`: the venue logo (issue #20), shown in place of the RingRing wordmark; null for none. */
  venue: { name: string; default_lang: Lang; logo_url: string | null };
  voice_langs: Lang[];
}

/** GET /staff/:pp/pickup-points: one counter of the venue with its open counts, for the staff screen's filter. */
export interface PickupPointInfo {
  id: string;
  name: string;
  waiting: number;
  ready: number;
}

/**
 * One open ticket on the staff board: from GET /staff/:pp/board and from a staff socket (same shape as the core's
 * StaffBoardEntry; never a phone number, never the device key). A TV board's socket gets the public board instead.
 */
export interface OpenTicket {
  id: string;
  displayNo: string;
  status: 'waiting' | 'ready';
  /** How the guest registered: whatsapp, web, sms, push or none. */
  channel: string;
  needsCall: boolean;
  createdAt: number;
  readyAt: number | null;
  /** The last alert attempt and how far it got; separate from the order state. */
  delivery: { channel: string; state: DeliveryState } | null;
  resends: number;
  lastResendAt: number | null;
  /** The WhatsApp call to the guest (issue #95): the automatic call's time, or the latest attempt's state. */
  call?: CallView | null;
}
/** The staff board. */
export interface BoardView {
  serviceDate: string;
  waiting: OpenTicket[];
  ready: OpenTicket[];
}

// The public board (TV boards, customer pages): display numbers and states, no ticket ids (issue #4).
export type { PublicBoard, PublicBoardEntry } from '@ringring/core';

/** A TV board's signed link (issue #16): the key its socket carries, and what to do when the link is revoked. */
export interface BoardLinkAccess {
  key: string;
  /** The server closed the socket because the link was revoked: the screen stops reconnecting and says so. */
  onRevoked: () => void;
}

/** Wait before reconnecting after a socket closed for any other reason (network, deploy, a refused upgrade). */
export const RECONNECT_MS = 3000;

/** The socket address of a pickup point for a staff screen (cookies decide) or a TV board (its link's key). */
export function boardSocketUrl(pickupPointId: string, role: 'board' | 'staff', key?: string): string {
  const base = `${WS_BASE}/ws/pickup/${encodeURIComponent(pickupPointId)}?role=${role}`;
  return key ? `${base}&${BOARD_LINK.param}=${encodeURIComponent(key)}` : base;
}

/**
 * Subscribe to a pickup point's live board; reconnects on its own, `RECONNECT_MS` after every close. The socket sends
 * the public board (display numbers and states) to a TV board opened with its signed link (`link.key`, issue #16), and
 * the staff board with ticket ids to `role=staff` from a signed-in staff device or an owner or manager session of the
 * venue; the server decides from the cookies and the key, so `B` only names what the caller expects. `onSettings` fires
 * on the `settings` message the venue settings send after a change (issue #11), so a staff screen refreshes its venue
 * at once. The `board_link_revoked` message or a close with `BOARD_LINK.revokedCloseCode` (the link was revoked)
 * calls `link.onRevoked` once and stops.
 */
export function subscribeBoard<B extends PublicBoard = BoardView>(
  pickupPointId: string,
  role: 'board' | 'staff',
  onBoard: (b: B) => void,
  onState: (online: boolean) => void,
  onSettings?: () => void,
  link?: BoardLinkAccess,
): () => void {
  let ws: WebSocket | null = null;
  let stopped = false;
  let timer: number | undefined;
  const connect = () => {
    if (stopped) return;
    ws = new WebSocket(boardSocketUrl(pickupPointId, role, link?.key));
    ws.onopen = () => onState(true);
    ws.onmessage = (ev) => {
      try {
        const m = JSON.parse(ev.data as string) as { type: string; board?: B };
        if (m.type === 'board' && m.board) onBoard(m.board);
        else if (m.type === 'settings') onSettings?.();
        else if (m.type === 'board_link_revoked' && link && !stopped) {
          // the server closes this socket right after; stop now rather than when the browser reports the close
          stopped = true;
          window.clearTimeout(timer);
          ws?.close();
          link.onRevoked();
        }
      } catch {
        // ignore
      }
    };
    ws.onclose = (ev: CloseEvent) => {
      // Once unsubscribed (or stopped by a revocation) the caller hears nothing more: a late close of the old socket
      // must not mark a newer one offline.
      if (stopped) return;
      onState(false);
      if (link && ev.code === BOARD_LINK.revokedCloseCode) {
        stopped = true;
        link.onRevoked();
        return;
      }
      timer = window.setTimeout(connect, RECONNECT_MS);
    };
  };
  connect();
  return () => {
    stopped = true;
    window.clearTimeout(timer);
    ws?.close();
  };
}
